import React, {useState, useEffect} from 'react';
import BN from 'bn.js';
import cx from 'classnames';

import Tooltip from 'components/common/tooltip/tooltip';
import InfoIcon from 'components/icons/InfoIcon';
import ButtonsGroup from 'components/button-group/button-group';
import { Input } from 'components/common/form/input';
import { Button } from 'components/common/button/button';
import { useModal } from 'components/modal-views/context';
import { formatToken } from 'components/wallet/token-amount';
import { formatTokenAmount, parseTokenAmount } from 'utils/amounts';
import { numeric, isDecimalString } from 'utils';
import { PlantCardType } from 'types';

import {
  StyledBalanceMount,
  StyledBalanceText,
  StyledButtonWrapper,
  StyledCardSide,
  StyledFlexGrow,
  StyledWrapper,
  StyledInputWrapper,
  StyledTopWrapper,
  StyledWrapperCheckbox, StyledLeftMount, StyledTopWrapperWithdraw, StyledFWrapBlock
} from './plant-card.style';
import Checkbox from "../../common/checkbox/checkbox";

type PlantCardProps = {
  item: PlantCardType;
  stateReset: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
};

const PlantCard = ({ item, stateReset }: PlantCardProps) => {
  const {
    name,
    title,
    icon: Icon,
    tooltipPosition,
    tooltipMessage,
    amount,
    textButton,
      date
  } = item;
  const [amountValue, setAmountValue] = useState('');
  const [fastWithdraw, fastWithdrawStatusSet] = useState(false);
  const { openModal } = useModal();
  const [reset, setReset] = stateReset;
  const getRawAmount = () => {
    if (!amountValue || amountValue === '.') {
      return '0';
    }
    return parseTokenAmount(amountValue, 24);
  };
  const isAmountZero = new BN(amount).isZero();

  const isValidAmount = () => {
    if (!amountValue || amountValue === '.') {
      return false;
    }

    return (
      !new BN(getRawAmount()).isZero() &&
      new BN(getRawAmount()).lte(new BN(amount)) &&
      isDecimalString(amountValue)
    );
  };

  useEffect(() => {
    if (name === 'collect' || name === 'rewards') {
      if (!isAmountZero && isDecimalString(amount)) {
        setAmountValue(formatTokenAmount(amount));
      }
    }
  }, [name, amount]);

  const onClickHandler = (value: string) => {
    if (value === '') {
      setAmountValue('');
      return;
    }
    setAmountValue(
      formatTokenAmount(((Number(amount) * Number(value)) / 100).toString())
    );
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value === '') {
      setAmountValue(value);
      return;
    }

    if (value === '.') {
      setAmountValue(value);
      return;
    }

    if (value.startsWith('.') && value.length > 1 && !value.includes('..')) {
      const formattedValue = '0' + value;
      setAmountValue(formattedValue);
      return;
    }

    if (value.match(/^\.\d+$/)) {
      const formattedValue = '0' + value;
      setAmountValue(formattedValue);
      return;
    }

    if (numeric(value)) {
      setAmountValue(value);
    }
  };

  // const returnPlantBlock = ():React.JSX.Element=>{
  //   return <StyledWithdrawalWrapper>
  //     <StyledWithdrawalBlock>
  //       <StyledTitleWithdrawal>
  //         Withdrawal Method
  //       </StyledTitleWithdrawal>
  //       <StyledBtnWithdrawalBlock>
  //         <StyledBtnWithdrawal>
  //           <Tooltip position='bottom-start'  message={'Receive your OPN for the cost of 30% of the OPN withdrawn'}>
  //             <StyledSpan>
  //             </StyledSpan>
  //           </Tooltip >
  //           Fast
  //         </StyledBtnWithdrawal>
  //         <StyledBtnWithdrawal>
  //           <Tooltip message={'Receive your OPN for no extra cost after a 90 day waiting period'}>
  //             <StyledSpan>
  //             </StyledSpan>
  //           </Tooltip >
  //           Delayed</StyledBtnWithdrawal>
  //       </StyledBtnWithdrawalBlock>
  //     </StyledWithdrawalBlock>
  //     <StyledWrapBtn>
  //     <Button
  //         fullwidth
  //         size="middle"
  //         disabled={!enterAmountIsComplete()}
  //         onClick={enterAmountIsComplete ? clickOpenModal : undefined}
  //     >
  //       {textButton}
  //     </Button>
  //     </StyledWrapBtn>
  //   </StyledWithdrawalWrapper>
  // }
  const enterAmountIsComplete = () =>
    name === 'collect' || name === 'rewards'
      ? amount && amount !== '0'
      : amountValue && isValidAmount();

  const clickOpenModal = () =>
    openModal('PLANT_VIEW', textButton, [getRawAmount()], (s)=> {
      setReset(s);
      setAmountValue('')
    });

  const dateToWithoutFee = ()=>{
    const currentDate = new Date();
    const withdrawDate = Number(date) / 1000000
    const target = new Date(withdrawDate);

    // Calculate the difference in milliseconds
    // @ts-ignore
    const differenceInMs = target - currentDate;

    // Convert milliseconds to days
    const daysDifference = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));

    return daysDifference>=0 ?daysDifference: 0
  }
  const allowFastWithdraw = name ==='withdraw'? !fastWithdraw &&  dateToWithoutFee():false;
  return (
    <StyledCardSide className={cx('side-front', `cs-${tooltipPosition}`)}>
      {tooltipMessage && (
        <Tooltip isMultiline message={tooltipMessage}>
          <InfoIcon color="#fabc73" />
        </Tooltip>
      )}
      <StyledTopWrapper>
        <StyledTopWrapperWithdraw>
          <StyledFWrapBlock>
        <StyledBalanceText>
          <Icon /> {title}:
        </StyledBalanceText>
        <StyledBalanceMount>{formatToken(amount, 24)}</StyledBalanceMount>
        </StyledFWrapBlock>
        {name === 'withdraw' && date&& <StyledLeftMount>{dateToWithoutFee()}  days left </StyledLeftMount>
        }
      </StyledTopWrapperWithdraw>
      </StyledTopWrapper>

      {(name === 'balance' || name === 'planted' || name === 'withdraw') && (
        <StyledWrapper>
          <StyledInputWrapper>
            <Input
              type="text"
              name="amount"
              placeholder="0"
              inputMode="decimal"
              className={cx({
                'is--error':
                  amountValue && amountValue !== '0' && !enterAmountIsComplete()
              })}
              disabled={isAmountZero}
              onChange={onChangeHandler}
              value={amountValue}
            />
          </StyledInputWrapper>
          <StyledFlexGrow>
            <StyledButtonWrapper>
              <ButtonsGroup
                disabled={isAmountZero}
                reset={reset}
                afterClick={onClickHandler}
              />
            </StyledButtonWrapper>
          </StyledFlexGrow>
        </StyledWrapper>
      )}
      {name ==='withdraw' && !!dateToWithoutFee() &&
      <StyledWrapperCheckbox>
        <Checkbox onChange={(checked) => fastWithdrawStatusSet(checked)} type="middle">
        <b>Fast Withdraw</b>
      </Checkbox>
        </StyledWrapperCheckbox>}
      {<Button
          fullwidth
          size="middle"
          disabled={!enterAmountIsComplete()  || !!allowFastWithdraw}
          onClick={enterAmountIsComplete  ? clickOpenModal : undefined}
      >
        {textButton}
      </Button>}
    </StyledCardSide>
  );
};

export default PlantCard;
